import { useAtom } from 'jotai';
import React, { FunctionComponent, useState } from 'react';
import { useQuery } from 'react-query';

import { Icon } from '@iconify/react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Description from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Tooltip
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { IFile } from '../models/IFile';
import {
  currentPageIndexAtom,
  isInitializingAtom,
  pageSizeAtom,
  refinerItemsAtom,
  searchKeywordAtom,
  selectedLocationIdAtom,
  selectedRefinerItemsAtom,
  selectedSitesAtom,
  startRowAtom,
  currentKQLQueryAtom,
  hitHighlightingEnabledAtom,
  currentSortQueryAtom,
} from '../utils/AtomConfig';
import Constants from '../utils/Constants';
import { MdiService } from '../utils/MdiService';
import FilePreview from './FilePreview';
import VideoPreview from './VideoPreview';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';

const useStyles = makeStyles({
  table: {
    '& td': {
      verticalAlign: 'top',
    },
    '& strong': {
      color: '#626262',
    },
  },
  icon: {
    fontSize: '1.9rem',
  },
});

export const SearchResult: FunctionComponent = () => {
  const mdiService = MdiService.Instance;
  const classes = useStyles();

  const [selectedLocationId] = useAtom(selectedLocationIdAtom);
  const [currentKQLQuery] = useAtom(currentKQLQueryAtom);
  const [currentSortQuery] = useAtom(currentSortQueryAtom);
  const [hitHighlightingEnabled] = useAtom(hitHighlightingEnabledAtom);
  const [selectedSites] = useAtom(selectedSitesAtom);
  const [searchKeyword] = useAtom(searchKeywordAtom);
  const [pageSize] = useAtom(pageSizeAtom);
  const [isInitializing, setIsInitializing] = useAtom(isInitializingAtom);
  const [currentPageIndex, setCurrentPageIndex] = useAtom(currentPageIndexAtom);
  const [startRow, setStartRow] = useAtom(startRowAtom);
  const [, setRefinerItems] = useAtom(refinerItemsAtom);
  const [selectedRefinerItems] = useAtom(selectedRefinerItemsAtom);
  const [selectedFile, setSelectedFile] = useState<IFile | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowMessage, setIsShowMessage] = useState(false);
  const theme = useTheme();

  function previewFile(file: IFile) {
    // Always open preview in new tab for mobile resolution
    if (
      window.matchMedia(theme.breakpoints.down('sm').replace('@media ', ''))
        .matches
    ) {
      window.open(file.preview);
      return;
    }

    // Set selected file
    setSelectedFile(file);

    // Check if file is video
    /*
    if (file.resourceType === 'Videos') {
      setShowVideoPreview(true);
      return;
    }*/

    switch (true) {
      case ['000', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf'].indexOf(
        file.fileType
      ) > -1:
        setShowPreview(true);
        break;
      default:
        window.open(file.preview);
        break;
    }
  }

  function downloadAction(url: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = url.split('/').pop() || '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function downloadFile(file: IFile) {
    // Check if file is 3D screen
    if (file.fileType === '000') {
      window.open(file.preview);
      return;
    }

    setIsLoading(true);
    downloadAction(file.downloadUrl);
    setTimeout(() => {
      setIsLoading(false);
      setIsShowMessage(true);
    }, 1000);
    return;
  }

  const itemsQuery = useQuery(
    [
      Constants.QUERY_KEYS.FILES,
      selectedLocationId,
      selectedSites,
      searchKeyword,
      pageSize,
      startRow,
      currentKQLQuery,
      selectedRefinerItems,
      startRow,
    ],
    () =>
      mdiService.GetListOfFiles(
        selectedLocationId.toString(),
        selectedSites,
        searchKeyword,
        pageSize,
        startRow,
        currentKQLQuery,
        selectedRefinerItems,
        currentSortQuery
      ),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
      enabled:
        selectedLocationId !== Constants.DEFAULT_ID && selectedSites.length > 0,
      onSuccess: (result) => {
        // Update refiner items
        setRefinerItems(result.refiners);

        setIsInitializing(false);
      },
    }
  );

  function getViewIconElement(fileType: string) {
    switch (true) {
      case ['doc', 'docx'].indexOf(fileType) > -1:
        return (
          <Icon
            icon='ant-design:file-word-filled'
            fr=''
            className={classes.icon}
          />
        );
      case ['xls', 'xlsx'].indexOf(fileType) > -1:
        return (
          <Icon
            icon='ant-design:file-excel-filled'
            fr=''
            className={classes.icon}
          />
        );
      case ['ppt', 'pptx'].indexOf(fileType) > -1:
        return (
          <Icon icon='ant-design:file-ppt-filled' fr='' className={classes.icon} />
        );
      case fileType === 'pdf':
        return (
          <Icon icon='ant-design:file-pdf-filled' fr='' className={classes.icon} />
        );
      case ['000'].indexOf(fileType) > -1:
        return <Icon icon='el:glasses' fr='' className={classes.icon} />;
      default:
        return <Description className={classes.icon} />;
    }
  }

  function getDownloadIconElement(fileType: string) {
    switch (true) {
      case ['000'].indexOf(fileType) > -1:
        return null;
      default:
        return <DownloadIcon className={classes.icon} />;
    }
  }

  function openPageResult(nextPage: number) {
    const _startRow = (nextPage - 1) * pageSize;
    setCurrentPageIndex(nextPage);
    setStartRow(_startRow);
  }

  function hidePreview() {
    setShowPreview(false);
    setShowVideoPreview(false);
    setSelectedFile(null);
  }

  const isLocationSelected = selectedLocationId !== Constants.DEFAULT_ID;
  const isSiteSelected = selectedSites.length > 0;

  return (
    <Box>
      <TableContainer component={Paper} elevation={3} sx={{ marginTop: 2 }}>
        <Table
          sx={{
            minWidth: 650,
          }}
          aria-label='search result'
          className={classes.table}
        >
          <TableHead>
          {(isLocationSelected && isSiteSelected) &&  (
            <TableRow
              sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                  color: '#ffffff',
                },
              }}
            >
              
              <TableCell sx={{ minWidth: 400 }}>Title</TableCell>
              <TableCell sx={{ width: 80 }}>View</TableCell>
              <TableCell sx={{ width: 110 }}>Download</TableCell>
              
            </TableRow>
            )}
          </TableHead>
          <TableBody
            sx={{
              '& tr:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                cursor: 'pointer',
              },
            }}
          >
            {/* Show loading spinner */}
            {itemsQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}

            {/* Show message if location is not selected */}
            {(!isLocationSelected || !isSiteSelected) && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                  <ReportGmailerrorredIcon sx={{color: '#6E6E6E'}} />
                  <Typography sx={{marginTop: '15px'}}>Please select a site from the left panel to display results.
                  </Typography>
                  <Typography sx={{marginTop: '15px', marginBottom: '15px'}}>You can also change your location with any of the options on the top of the page.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* Show no items result */}
            {itemsQuery.data?.files.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                  0 matching results. Please try a different search query.
                </TableCell>
              </TableRow>
            )}

            {/* Show items */}
            {itemsQuery.data?.files.map((file, index) => (
              <TableRow
                key={`${index}-${file.fileId}`}
                onDoubleClick={() =>
                  file.isDownloadOnly || !file.preview
                    ? downloadFile(file)
                    : previewFile(file)
                }
              >
                <TableCell>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    {file.title}
                  </div>
                  {/* Show hithighlighting if enabled */}
                  {hitHighlightingEnabled && (
                    <div
                      style={{ fontSize: 13, color: '#878787' }}
                      dangerouslySetInnerHTML={{
                        __html: file.hitHightlights
                          ? file.hitHightlights + '...'
                          : '',
                      }}
                    ></div>
                  )}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                {(file.isDownloadOnly) && (
                  <Tooltip title={"You cannot preview " + file.fileType + " files over 10MB."}>
                    <span>
                      <IconButton
                        onClick={() => previewFile(file)}
                        disabled={file.isDownloadOnly || !file.preview}
                      >
                        {getViewIconElement(file.fileType)}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {(!file.isDownloadOnly) && (
                  <IconButton
                    onClick={() => previewFile(file)}
                    disabled={file.isDownloadOnly || !file.preview}
                  >
                    {getViewIconElement(file.fileType)}
                  </IconButton>
                )}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <IconButton
                    onClick={() => downloadFile(file)}
                    disabled={!file.downloadUrl}
                  >
                    {getDownloadIconElement(file.fileType)}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedFile && (
        <>
          <FilePreview
            title={selectedFile.title}
            show={showPreview}
            fileSource={selectedFile.preview}
            is3dScan={selectedFile.fileType === '000'}
            onClose={hidePreview}
          ></FilePreview>
          <VideoPreview
            show={showVideoPreview}
            fileSource={selectedFile.preview}
            title={selectedFile.title}
            onClose={hidePreview}
          ></VideoPreview>
        </>
      )}
      {/* Pagination */}
      <Box sx={{ padding: 2, textAlign: 'center' }}>
        <ButtonGroup variant='outlined'>
          <Button
            startIcon={<ArrowBackIosNewIcon />}
            disabled={itemsQuery.isLoading || currentPageIndex === 1}
            onClick={() => openPageResult(currentPageIndex - 1)}
          >
            Prev
          </Button>
          <Button
            endIcon={<ArrowForwardIosIcon />}
            disabled={
              isInitializing ||
              itemsQuery.isLoading ||
              itemsQuery.data?.lastPage
            }
            onClick={() => openPageResult(currentPageIndex + 1)}
          >
            Next
          </Button>
        </ButtonGroup>
      </Box>
      {/* Dropbox */}
      <Backdrop sx={{ color: '#fff' }} open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      {/* Download message */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={isShowMessage}
        autoHideDuration={3000}
        onClose={() => setIsShowMessage(false)}
        message={'File has been requested. Please wait for a few seconds.'}
      />
    </Box>
  );
};
